import * as React from 'react';
import { Link } from 'react-router-dom';
import { useSubdomains } from '../state/subdomain-state';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

/**
 * Subdomain list page — displays all subdomains assigned to the current user.
 * Validates: Requirement 7.2 — display list of subdomains assigned to user.
 */
export function SubdomainListPage(): React.JSX.Element {
  const { state, loadSubdomains, isConnecting } = useSubdomains();

  // Connecting state (cold start retry in progress)
  if (isConnecting) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12" aria-busy="true">
        <Spinner label="Connecting..." />
        <p className="text-sm text-muted-foreground">Establishing connection to the server...</p>
      </div>
    );
  }

  // Loading state
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-12" aria-busy="true">
        <Spinner label="Loading subdomains..." />
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="py-12">
        <Alert variant="destructive">
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
        <div className="mt-4 flex justify-center">
          <Button onClick={loadSubdomains} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (state.subdomains.length === 0) {
    return (
      <section className="py-12 text-center">
        <h2 className="text-lg font-semibold text-foreground">No Subdomains Assigned</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have any subdomains assigned yet. Contact your administrator to get started.
        </p>
      </section>
    );
  }

  // Subdomain list
  return (
    <section aria-label="Assigned subdomains">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">My Subdomains</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage API keys for your assigned subdomains. Each subdomain gives you programmatic control over its DNS records.
        </p>
      </div>

      {/* How it works guide */}
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">How it works</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Click a subdomain below to manage its API keys</li>
            <li>Generate an API key (valid from 1 hour to permanent)</li>
            <li>Use the key to create, update, or delete DNS records via the REST API</li>
            <li>Records are scoped to your subdomain — you can only modify records within it</li>
          </ol>
          <div className="mt-3 space-y-2 text-xs text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">Team access:</span> Multiple users can be assigned to the same subdomain. Each user generates their own keys, and all users share read/write access to the subdomain's DNS records.
            </p>
            <p>
              <span className="font-medium text-foreground">Common use cases:</span> SSL cert validation (Let's Encrypt DNS-01, AWS ACM), pointing subdomains to servers/load balancers, and automated infrastructure provisioning via CI/CD.
            </p>
            <p>
              <span className="font-medium text-foreground">AI tools:</span> After generating a key, expand the "Copy-pasteable API Reference" section — paste it into Kiro or Copilot and ask it to integrate DNS management into your project.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {state.subdomains.map((subdomain) => (
          <Link
            key={subdomain.subdomainId}
            to={`/subdomains/${subdomain.subdomainId}/keys`}
            className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
          >
            <Card className="transition-colors hover:border-primary/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium">
                  {subdomain.subdomainLabel}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{subdomain.fqdn}</Badge>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Assigned {new Date(subdomain.assignedAt).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
